/**
 * @file   src/helpers/mapbox.js
 * @brief  Adaptive framing helpers shared by the Mapbox map + screenshot components
 */

/**
 * Fallback map style used when REACT_APP_MAPBOX_STYLE is not configured.
 * Streets keeps highway shields, town/place labels and landmark POIs (airports,
 * etc.) that anchor orientation, without the traffic congestion coloring.
 */
const DEFAULT_MAP_STYLE = "mapbox/streets-v12";

/**
 * Resolve the active map style from config.
 *
 * REACT_APP_MAPBOX_STYLE lets each environment point at a different style
 * (e.g. a custom Studio style) without a code change. Value is "owner/styleId"
 * (e.g. "gitcha/clabc123"); falls back to Streets.
 *
 * @returns {{owner: string, styleId: string, url: string}} Forms needed by the
 *   Static Images API (owner + styleId) and mapbox-gl (mapbox:// url)
 */
export const getMapStyleRef = () => {
  const ref = process.env.REACT_APP_MAPBOX_STYLE || DEFAULT_MAP_STYLE;
  const slash = ref.indexOf("/");
  const owner = slash === -1 ? "mapbox" : ref.slice(0, slash);
  const styleId = slash === -1 ? ref : ref.slice(slash + 1);
  return { owner, styleId, url: `mapbox://styles/${owner}/${styleId}` };
};

/**
 * Upscale small static-map dimensions so Mapbox renders more town/landmark
 * labels - label density scales with the requested pixel size, so a tiny card
 * comes back nearly empty. The image is then downscaled by CSS to fit the card.
 * Never shrinks larger maps, and caps the long edge to stay within Static API
 * size limits.
 *
 * @param {number} width - Display width in px
 * @param {number} height - Display height in px
 * @returns {{width: number, height: number}} Source dimensions to request
 */
export const detailDimensions = (width, height) => {
  const scale = Math.max(1, Math.min(2, 600 / width, 600 / height));
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

/**
 * Context zoom-out factor for a search area.
 *
 * Framing the view tightly to the search area hides the surrounding towns,
 * highways, and labels that tell a user where the area actually is. This pulls
 * the view back by a factor that scales inversely with size: tight areas (which
 * have the least context) zoom out most, large areas barely change.
 *
 * Examples: 1mi -> 1.8x, 5mi -> 1.47x, 20mi -> 1.31x.
 *
 * @param {number} radiusMiles - Radius (or equivalent radius) of the area in miles
 * @returns {number} Multiplier in the range [1.25, 1.8]
 */
export const getContextFactor = (radiusMiles) => {
  const r = Number(radiusMiles);
  if (!r || r <= 0 || isNaN(r)) return 1.5;
  return Math.max(1.25, Math.min(1.8, 1.25 + 1.1 / r));
};

/**
 * Approximate the radius of a bounding box in miles.
 *
 * Lets polygon areas (custom draws, zip boundaries) reuse the same radius-based
 * context zoom as circular searches.
 *
 * @param {number[]} bounds - [minLng, minLat, maxLng, maxLat]
 * @returns {number} Half of the larger box dimension, in miles
 */
export const boundsToRadiusMiles = ([minLng, minLat, maxLng, maxLat]) => {
  const midLat = (minLat + maxLat) / 2;
  const latMiles = (maxLat - minLat) * 69;
  const lngMiles = (maxLng - minLng) * 69 * Math.cos((midLat * Math.PI) / 180);
  return Math.max(latMiles, lngMiles) / 2;
};

/**
 * Grow a bounding box around its center by a factor, adding surrounding context.
 *
 * @param {number[]} bounds - [minLng, minLat, maxLng, maxLat]
 * @param {number} factor - Multiplier applied to the box half-extents
 * @returns {number[]} Expanded [minLng, minLat, maxLng, maxLat]
 */
export const expandBounds = ([minLng, minLat, maxLng, maxLat], factor) => {
  const cx = (minLng + maxLng) / 2;
  const cy = (minLat + maxLat) / 2;
  const hx = ((maxLng - minLng) / 2) * factor;
  const hy = ((maxLat - minLat) / 2) * factor;
  return [cx - hx, cy - hy, cx + hx, cy + hy];
};

/**
 * Convenience: expand a bounding box by the adaptive context factor derived
 * from its own size.
 *
 * @param {number[]} bounds - [minLng, minLat, maxLng, maxLat]
 * @returns {number[]} Expanded [minLng, minLat, maxLng, maxLat]
 */
export const expandBoundsForContext = (bounds) =>
  expandBounds(bounds, getContextFactor(boundsToRadiusMiles(bounds)));

/**
 * Whether a map <img> URL (stored or live) is safe to render as-is, or has
 * to be treated as unavailable because it's the specific URL that most
 * recently failed to load. The single source of truth for this decision -
 * every place that decides whether to display a URL, or build a new one to
 * replace it, must agree, or the component can end up showing two sources
 * at once (an extra billed live request alongside a stored image) or
 * neither (a blank card).
 *
 * Compares against the URL itself, not a bare "has this ever failed"
 * boolean, because feed lists key cards by index - the same
 * StaticMapboxComponent instance gets reused for a different want-ad on
 * filter/sort/pagination. A boolean would stick a failure recorded for one
 * URL onto whatever URL replaces it, permanently forcing that card slot
 * onto the billed live path (or a blank card, for the live-URL case) for
 * the rest of the session; comparing the URLs means a fresh one always
 * gets a clean chance regardless of what failed before it. Used for both
 * storedMapUrl/failedUrl and mapUrl/failedLiveUrl in
 * StaticMapboxComponent - same shape, same reuse hazard, either source.
 *
 * @param {Object} input
 * @param {string} [input.url] - the URL currently being considered
 * @param {string|null} [input.failedUrl] - the URL whose <img> most
 *   recently fired onError this session, or null/undefined if none has -
 *   never retried, but only for that specific URL
 * @returns {boolean}
 */
export const isMapUrlUsable = ({ url, failedUrl }) =>
  Boolean(url) && failedUrl !== url;

/**
 * Whether StaticMapboxComponent needs to build (and bill) a live Mapbox
 * Static Images URL, or can just render a pre-rendered image the API
 * already handed us.
 *
 * A usable stored URL needs no boundary wait (unlike the live path, a
 * stored image was rendered server-side with its own zip geometry already
 * resolved), and no visibility gating either: an <img src> is cheap to have
 * off-screen, unlike a live Mapbox request, so there's no billing reason to
 * delay it the way isVisible delays the live path below.
 *
 * @param {Object} input
 * @param {string} [input.storedMapUrl] - pre-rendered image URL from the API,
 *   if the backend has one for this want-ad + card variant
 * @param {string|null} [input.failedUrl] - see isMapUrlUsable
 * @param {boolean} input.isVisible - the existing lazy-load gate (only
 *   relevant to the live path - a stored image has no per-request cost)
 * @param {boolean} input.pendingPolygon - the existing "boundaries not back
 *   yet" gate (only relevant to the live path)
 * @returns {boolean} true if the live-generation effect should run
 */
export const shouldGenerateLiveMap = ({
  storedMapUrl,
  failedUrl,
  isVisible,
  pendingPolygon,
}) => {
  if (isMapUrlUsable({ url: storedMapUrl, failedUrl })) return false;
  return isVisible && !pendingPolygon;
};

/**
 * Parse a MySQL ST_AsText(POLYGON(...)) string into the nested coordinate
 * shape StaticMapboxComponent/MapboxComponent expect.
 *
 * @param {string|null|undefined} wkt - e.g. "POLYGON((-101 33, -100 33, ...))"
 * @returns {Array|null} [[{lng, lat}, ...]] or null if unparseable/empty
 */
export const parseDrawnPolygonWKT = (wkt) => {
  if (!wkt) return null;
  const match = wkt.match(/POLYGON\(\((.+)\)\)/i);
  if (!match) return null;
  const coords = match[1].split(",").map((pair) => {
    const [lng, lat] = pair.trim().split(/\s+/).map(Number);
    return { lng, lat };
  });
  return [[coords]];
};

/**
 * Normalize a want-ad's zipCode field to the {zipCode, zipId} shape
 * usePolygons/the feed cards expect. Some endpoints (favorites list) return
 * bare zip strings instead of objects.
 *
 * @param {Array|null|undefined} zipCodes
 * @returns {Array} Always an array, each entry shaped {zipCode, ...}
 */
export const normalizeZipCodes = (zipCodes) =>
  (zipCodes || []).map((zip) =>
    typeof zip === "string" ? { zipCode: zip } : zip,
  );

/**
 * A center coordinate is only usable if both parts are real, finite numbers -
 * an empty string or null coerces to 0 via Number(), which silently frames
 * the map on (0, 0) in the Gulf of Guinea instead of falling back cleanly.
 *
 * @param {*} lng
 * @param {*} lat
 * @returns {boolean}
 */
export const hasUsableCoordinates = (lng, lat) =>
  Number.isFinite(Number(lng)) && Number.isFinite(Number(lat)) &&
  lng !== null && lng !== "" && lat !== null && lat !== "";

export const hasDrawableArea = (coordinates) => {
  const points = (coordinates || []).filter(
    (point) => Array.isArray(point) && Number.isFinite(point[0]) && Number.isFinite(point[1]),
  );
  if (new Set(points.map(([lng, lat]) => `${lng},${lat}`)).size < 3) return false;
  const lngs = points.map(([lng]) => lng);
  const lats = points.map(([, lat]) => lat);
  return Math.max(...lngs) > Math.min(...lngs) && Math.max(...lats) > Math.min(...lats);
};

const ZIP_FALLBACK_RADIUS_KM = 1.6;

const circleRing = ([lng, lat], radiusKm, steps = 48) => {
  const kmPerDegreeLat = 111.32;
  const kmPerDegreeLng = 111.32 * Math.cos((lat * Math.PI) / 180);
  const ring = [];
  for (let i = 0; i < steps; i++) {
    const angle = (i * 2 * Math.PI) / steps;
    ring.push([
      lng + (radiusKm * Math.cos(angle)) / kmPerDegreeLng,
      lat + (radiusKm * Math.sin(angle)) / kmPerDegreeLat,
    ]);
  }
  ring.push(ring[0]);
  return ring;
};

const isRealPoint = (point) =>
  Array.isArray(point) &&
  Number.isFinite(point[0]) &&
  Number.isFinite(point[1]) &&
  !(point[0] === 0 && point[1] === 0);

export const zipShapes = (rings, radiusKm = ZIP_FALLBACK_RADIUS_KM) =>
  (rings || [])
    .map((ring) => (ring || []).filter(isRealPoint))
    .filter((ring) => ring.length > 0)
    .map((ring) => {
      if (hasDrawableArea(ring)) {
        const [firstLng, firstLat] = ring[0];
        const [lastLng, lastLat] = ring[ring.length - 1];
        // Dropping 0,0 points can leave the ring open; GeoJSON needs it closed.
        return firstLng === lastLng && firstLat === lastLat ? ring : [...ring, ring[0]];
      }
      const lng = ring.reduce((sum, [x]) => sum + x, 0) / ring.length;
      const lat = ring.reduce((sum, [, y]) => sum + y, 0) / ring.length;
      return circleRing([lng, lat], radiusKm);
    });
